import { apiFetch, apiFetchBlob, apiFetchRaw, apiUpload } from './client.js';

/** An IBANQ sub-account request as the customer page shows it (DEV-149). */
export interface IbanqSubAccountView {
  /** IBANQ's word: SENDING, Pending, Accepted, Active, Rejected, Failed. */
  status: string;
  state: 'PENDING' | 'STALLED' | 'ACTIVE' | 'REFUSED';
  requestId: string | null;
  /** Masked. */
  iban: string | null;
  rejectionReason: string | null;
  rejectionDetail: string | null;
  lastError: string | null;
  requestedAt: string;
  lastCheckedAt: string | null;
  nextCheckAt: string | null;
  activatedAt: string | null;
}

export type CustomerStatus = 'ACTIVE' | 'SUSPENDED' | 'FROZEN' | 'CLOSED';

/** One row of the customer book. */
/** The company's own sanctions screening, apart from its people (DEV-89). */
export interface CompanyScreening {
  state: 'NOT_STARTED' | 'IN_PROGRESS' | 'CLEAR' | 'MATCHES_FOUND' | 'FAILED' | null;
  providerStatus: string | null;
  matchCount: number;
  requestId: number | null;
  screenedAt: string | null;
  failureReason: string | null;
  /** The provider's existing check is shown; the latest re-screen was refused. */
  rescreenRefused: boolean;
  overallClassification: string | null;
  startedAt: string | null;
  report: { documentId: string; filename: string; filedAt: string } | null;
}

export interface CustomerListRow {
  customerId: string;
  /** Legal name for a business, the person's name for an individual. */
  name: string;
  email: string;
  phone: string;
  accountType: string;
  country: string;
  status: string;
  riskRating: string;
  applicationId: string | null;
  applicationStatus: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface CustomerListFilters {
  search?: string;
  status?: string;
  accountType?: string;
  country?: string;
  sort?: 'created' | 'updated' | 'name';
  direction?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CustomerPage {
  entries: CustomerListRow[];
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
}

export interface CustomerSearchResult {
  customerId: string;
  maskedName: string;
  maskedEmail: string;
  maskedPhone: string;
  status: string;
}

export interface Customer360Accounts {
  accountId: string;
  iban: string;
  currency: string;
  availableBalanceCents: string;
  settledBalanceCents: string;
  status: string;
  /** Numeric proto AccountType: 1 = CURRENT, 2 = SAVINGS. */
  accountType: string;
  /** Institution holding the account, e.g. NORVIX. */
  provider: string;
  /** Set where customers share one partner account and are told apart by reference. */
  paymentReference: string;
  /** '' | 'AWAITING_IBAN' | 'ISSUED' | 'REJECTED' */
  providerStatus: string;
}

export interface Customer360Card {
  cardId: string;
  last4: string;
  status: string;
  cardType: string;
}

export interface Customer360Transaction {
  id: string;
  type: string;
  amountCents: string;
  currency: string;
  occurredAt: string;
  status: string;
  /** What the movement was, e.g. "FEE: SEPA Incoming Transfer". */
  description?: string;
  referenceId?: string;
}

export interface Customer360 {
  customerId: string;
  /** null means no application; absent means the lookup was unavailable. */
  applicationId?: string | null;
  identity: {
    displayName?: string;
    maskedName: string;
    maskedEmail: string;
    maskedPhone: string;
    maskedDob: string;
    nationality: string;
  };
  kyc: {
    status: string;
    riskRating: string;
    lastReviewDate: string | null;
    eddStatus: string | null;
  };
  accounts: Customer360Accounts[];
  cards: Customer360Card[];
  recentTransactions: Customer360Transaction[];
  /** When and by whom the client was approved at onboarding; null if not approved. */
  approval?: { approvedAt: string; approvedBy: string | null } | null;
  /** The periodic review schedule, derived from the risk band. */
  review?: {
    lastReviewAt: string | null;
    nextReviewAt: string | null;
    overdue: boolean;
  };
  /** What has been collected, and whether each is still good. */
  documents?: Array<{
    id: string;
    /** The file's current name; null for a record with no file behind it. */
    name?: string | null;
    /** VAULT files can be renamed and removed here; KYC records cannot. */
    origin?: 'VAULT' | 'KYC';
    documentType: string;
    status: string;
    expiresAt: string | null;
    issuedAt: string | null;
    collectedAt: string;
    /** VALID | EXPIRING | EXPIRED | STALE | NO_EXPIRY */
    validity: string;
    validityDetail: string;
    needsAttention: boolean;
  }>;
  /** Which account this client's fees are taken from; null means the default. */
  /** BUSINESS or INDIVIDUAL, as declared on the application. */
  accountType?: string;
  /** Present only for a business customer. A legal name is register data, not PII. */
  business: {
    legalName: string;
    registrationNumber: string;
    incorporationCountry: string;
    registryVerificationStatus: string;
    /** GuruPay's industry category (1–20) chosen for the company; null until somebody has. */
    gurupayIndustryType: number | null;
    /** IBANQ's GICS sub-industry code (8 digits) staff chose; null until somebody has (DEV-149). */
    ibanqGicsCode?: number | null;
  } | null;
  /** Objects we created at the identity provider, with deep links to its console. */
  providerReferences: {
    provider: string;
    entityType: string;
    providerId: string;
    role: string;
    displayName: string | null;
    screeningState: string | null;
    screeningRequestId: string | null;
    createdAt: string;
    /** null when no provider console URL is configured. */
    providerUrl: string | null;
  }[];
  complianceFlags: {
    /** null when the compliance database could not be reached — not zero. */
    openAlertsCount: number | null;
    /** Real latest classification, or 'NOT_SCREENED' when none has been run. */
    sanctionsScreeningStatus: string;
    /** When that screening ran; null when it never has. */
    screenedAt?: string | null;
    hasOpenSar: boolean | null;
    /** False when alert data could not be loaded at all. */
    alertsAvailable?: boolean;
  };
  consents: {
    type: string;
    granted: boolean;
    grantedAt: string | null;
    revokedAt: string | null;
  }[];
  tags: string[];
}

export type PiiFieldType = 'email' | 'phone' | 'name' | 'dob' | 'address';

/** One fact about an identity check, as the provider stated it. */
export interface EvidenceField {
  label: string;
  value: string;
  group: string;
}

/** The record behind an identity decision — what a reviewer files. */
export interface ProviderFile {
  id: string;
  name: string;
  kind: string;
  status: string;
  sizeBytes: number;
  /** Produced by the hosted identity check (document capture, selfie, report). */
  fromVerification: boolean;
}

/** A check artefact held in Norvix's own vault. */
export interface HeldFile {
  id: string;
  filename: string;
  sizeBytes: number;
  status: string;
  partyName: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export interface PartyEvidence {
  decision: string;
  providerStatus: string;
  fields: EvidenceField[];
  files: ProviderFile[];
  checkCount: number;
  readAt: string;
  fullName: string;
  role: string;
  providerConsoleUrl: string | null;
}

export interface VerificationParty {
  providerId: string;
  fullName: string;
  role: string;
  inScope: boolean;
  scopeReason: string;
  canStart: boolean;
  blockedReason: string;
  screeningState: string;
  /** PENDING | APPROVED | REJECTED | EDD_REQUIRED — read back from the provider. */
  identityDecision: string;
  /** The provider's raw status text behind that decision. */
  identityStatus: string;
  /** Stored link, reused until near expiry; empty when none or expired. */
  verificationLink: string;
  verificationLinkIssuedAt: string;
}

/** Ours against the provider's; document numbers masked to their last four. */
export interface PersonComparison {
  applicantDateOfBirth: string;
  providerDateOfBirth: string;
  dateOfBirthMatch: boolean;
  /** Same year, day and month exchanged — never a match. */
  dateOfBirthSwapped: boolean;
  applicantCitizenship: string;
  providerCitizenship: string;
  citizenshipCompared: boolean;
  citizenshipMatch: boolean;
  applicantDocumentMasked: string;
  providerDocumentsMasked: string[];
  documentCompared: boolean;
  documentMatch: boolean;
}

/** A person the provider refused as a duplicate and that was not linked (DEV-126). */
export interface DuplicatePersonRefusal {
  id: string;
  personName: string;
  role: string;
  reason: string;
  /** OPEN — waiting; ATTACHED — staff attached a profile, not yet sent. */
  status: string;
  attachedProviderId: string;
  refusedAt: string;
  candidates: { providerId: string; name: string; consoleUrl: string }[];
  comparison: PersonComparison | null;
}

export interface AttachExistingPersonResult {
  providerId: string;
  personName: string;
  role: string;
  comparison: PersonComparison | null;
  mismatches: string[];
  recorded: boolean;
  push: { providerId: string; directorsAdded: number; beneficialOwnersAdded: number } | null;
  /** Why the continue stopped, e.g. the next person refused; '' when it went through. */
  pushError: string;
  verificationLink: string;
  linkError: string;
}

/** Payment sources plus actual posted customer fees, for the client-profile history. */
export type PaymentSource = 'SEPA' | 'SWIFT' | 'INBOUND' | 'WITHDRAWAL' | 'FEE';

/** A single enriched payment in the client profile's history (tracker #43). */
export interface CustomerPaymentRow {
  id: string;
  source: PaymentSource;
  direction: 'IN' | 'OUT';
  /** Payer (money in) or beneficiary (money out); null when the source held none. */
  counterpartyName: string | null;
  /** Their account, masked. Full via revealPaymentIban (audited). */
  counterpartyIban: string | null;
  reference: string | null;
  amountCents: string;
  currency: string;
  status: string;
  occurredAt: string;
  feeAccountId?: string;
  /** Set when the counterparty is a Norvix client too: link to their profile. */
  counterpartyCustomerId?: string;
  counterpartyCustomerName?: string;
}

export interface CustomerPaymentsPage {
  items: CustomerPaymentRow[];
  hasMore: boolean;
  /** At least one source hit its window — older rows of a busy source may be missing. */
  truncated: boolean;
  /** At least one source failed to answer — the history shown is partial. */
  degraded: boolean;
}

export const customersApi = {
  /** Nominate the account fees come from. Null restores the default. */

  /**
   * A short-lived READ-ONLY session for viewing the customer's own interface.
   *
   * Development and QA only — the gateway refuses outright in production. The
   * token it returns may read the customer's pages and nothing else: every
   * request that could change something is refused before it reaches a route,
   * and every request is logged against the admin who made it.
   */
  impersonate: async (
    customerId: string,
    reason: string,
  ): Promise<{ accessToken: string; expiresAt: number; readOnly: boolean }> =>
    apiFetch(`/admin/customers/${customerId}/impersonate`, {
      method: 'POST',
      body: { reason },
    }),

  /** One page of the customer book, filtered and sorted. */
  list: async (filters: CustomerListFilters = {}): Promise<CustomerPage> => {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === '') continue;
      qs.set(key, String(value));
    }
    const query = qs.toString();
    const envelope = await apiFetchRaw<CustomerListRow[]>(
      `/admin/customers${query === '' ? '' : `?${query}`}`,
    );
    const entries = envelope.data ?? [];
    return {
      entries,
      total: envelope.meta?.total ?? entries.length,
      page: envelope.meta?.page ?? 1,
      limit: envelope.meta?.limit ?? entries.length,
      hasNext: envelope.meta?.hasNext ?? false,
    };
  },

  listAll: (limit = 100): Promise<CustomerSearchResult[]> =>
    apiFetch<CustomerSearchResult[]>(`/admin/customers?limit=${String(limit)}`),

  search: (q: string, limit = 20): Promise<CustomerSearchResult[]> =>
    apiFetch<CustomerSearchResult[]>(
      `/admin/customers/search?q=${encodeURIComponent(q)}&limit=${String(limit)}`,
    ),

  getCustomer: (id: string): Promise<Customer360> =>
    apiFetch<Customer360>(`/admin/customers/${id}`),

  revealPii: (id: string, fieldType: PiiFieldType): Promise<{ value: string }> =>
    apiFetch<{ value: string }>(`/admin/customers/${id}/reveal-pii`, {
      method: 'POST',
      body: { fieldType },
    }),

  addTag: (id: string, tag: string): Promise<{ success: boolean }> =>
    apiFetch<{ success: boolean }>(`/admin/customers/${id}/tags`, {
      method: 'POST',
      body: { tag },
    }),

  /**
   * Set the risk rating by hand, overriding the computed one. Records who and
   * why, and marks the rating manual so a re-score does not revert it.
   */
  setRiskRating: (
    id: string,
    rating: 'LOW' | 'MEDIUM' | 'HIGH' | 'PROHIBITED',
    reason: string,
  ): Promise<{ ok: boolean; rating: string; source: string }> =>
    apiFetch(`/admin/customers/${id}/risk-rating`, {
      method: 'POST',
      body: { rating, reason },
    }),

  /**
   * A page of the customer's payment history, merged across all four sources
   * (SEPA, SWIFT, inbound, withdrawals), newest first. Counterparty accounts
   * are masked; use revealPaymentIban for the full one. Tracker #43.
   */
  payments: async (id: string, limit = 25, offset = 0, includeFees = true): Promise<CustomerPaymentsPage> => {
    const env = await apiFetchRaw<CustomerPaymentRow[]>(
      `/admin/customers/${id}/payments?limit=${String(limit)}&offset=${String(offset)}&includeFees=${String(includeFees)}`,
    );
    return {
      items: env.data ?? [],
      hasMore: env.meta?.hasMore ?? false,
      truncated: env.meta?.truncated ?? false,
      degraded: env.meta?.degraded ?? false,
    };
  },

  /** The full counterparty account for one payment — audited server-side. */
  revealPaymentIban: (
    id: string,
    source: PaymentSource,
    paymentId: string,
  ): Promise<{ iban: string }> =>
    apiFetch<{ iban: string }>(`/admin/customers/${id}/payments/reveal-iban`, {
      method: 'POST',
      body: { source, paymentId },
    }),

  /** The customer's IBANQ sub-account request: where IBANQ's review stands (DEV-149). Null when none was made. */
  ibanqSubAccount: (id: string): Promise<IbanqSubAccountView | null> =>
    apiFetch<IbanqSubAccountView | null>(`/admin/customers/${id}/ibanq-sub-account`),

  /** Ask IBANQ now instead of waiting for the next back-off (account openers only). */
  refreshIbanqSubAccount: (id: string): Promise<IbanqSubAccountView | null> =>
    apiFetch<IbanqSubAccountView | null>(`/admin/customers/${id}/ibanq-sub-account/refresh`, { method: 'POST', body: {} }),

  /**
   * Open this customer's account by hand. Approval does it automatically now;
   * this is for customers approved before it did, and for retrying a failure.
   */
  openAccount: (
    id: string,
    input: {
      reason: string;
      currency: string;
      productType: string;
      provider: string;
      /** For a business opened at GuruPay: sent only when none is stored or it was changed. */
      gurupayIndustryType?: number;
      /** IBANQ_SUBACCOUNT, a business with none on file: its GICS sub-industry code. */
      ibanqGicsCode?: number;
    },
  ): Promise<{
    /** What happened, in the partner's own terms. */
    summary: string;
    /** True when the partner accepted but has not issued the IBAN yet. */
    awaitingPartner: boolean;
    accounts: {
      accountId: string;
      iban: string;
      currency: string;
      status: string;
      provider: string;
      paymentReference: string;
      providerStatus: string;
    }[];
  }> => apiFetch(`/admin/customers/${id}/accounts`, { method: 'POST', body: input }),

  /**
   * Which institutions can hold an account, and why the others cannot. Served
   * by the API so a provider that is not connected is never offered.
   */
  accountProviders: (): Promise<{
    providers: {
      id: string;
      label: string;
      description: string;
      enabled: boolean;
      disabledReason: string;
      /** The offered currencies it can open today (DEV-162); empty means none. */
      currencies: string[];
      /** The offered currencies it cannot open, with the reason. */
      unavailableCurrencies: { currency: string; reason: string }[];
      /** Said once about its list, e.g. that IBANQ was not asked. */
      currencyNote?: string;
    }[];
    /** Settings → Organisation → Currencies offered, in order (DEV-162). */
    offeredCurrencies: string[];
    productTypes: string[];
    defaultProvider: string;
  }> => apiFetch('/admin/account-providers'),

  /**
   * Privileged actions taken against this customer, and the reasons given.
   * Includes attempts that failed — which is the case an investigation wants.
   */
  actions: (
    id: string,
  ): Promise<
    Array<{
      id: string;
      adminId: string;
      adminEmail: string | null;
      action: string;
      subjectId: string | null;
      subjectType: string | null;
      reason: string;
      outcome: string;
      detail: unknown;
      createdAt: string;
    }>
  > => apiFetch(`/admin/customers/${id}/actions`),

  /** Parties (UBOs, directors) on a business application. */
  verificationParties: (
    id: string,
  ): Promise<{
    companyProviderId: string;
    parties: VerificationParty[];
    /** People the provider refused as duplicates, waiting for staff (DEV-126). */
    refusals?: DuplicatePersonRefusal[];
  }> => apiFetch(`/admin/customers/${id}/verification-parties`),

  /**
   * Attach the Platforma365 person a duplicate refusal named, then send the
   * application again and issue the person's link (DEV-126). Admin and
   * AML/MLRO only.
   */
  attachExistingPerson: (
    id: string,
    body: { refusalId: string; providerPersonId: string; role: string; reason: string },
  ): Promise<AttachExistingPersonResult> =>
    apiFetch(`/admin/customers/${id}/verification-parties/attach-existing-person`, {
      method: 'POST',
      body,
    }),

  /**
   * Push a stored application to the provider configured now. For a customer
   * whose original submission never reached one — sandbox credentials, an
   * outage, a half-configured console — this is the only way to create the
   * company and person profiles that everything else depends on.
   */
  createProviderProfile: (
    id: string,
    options?: { force?: boolean; companyProviderId?: string },
  ): Promise<{
    providerId: string;
    directorsAdded: number;
    beneficialOwnersAdded: number;
    screeningTriggered: boolean;
    attachedToExisting: boolean;
    personsReused: number;
    detailSynced: boolean;
    detailSyncError?: string;
  }> =>
    apiFetch(
      `/admin/customers/${id}/provider-profile${options?.force === true ? '?force=true' : ''}`,
      {
        method: 'POST',
        ...(options?.companyProviderId !== undefined
          ? { body: { companyProviderId: options.companyProviderId } }
          : {}),
      },
    ),

  /**
   * The applicant's own hosted verification link, for a personal application.
   *
   * Resumes the existing profile rather than creating one — a second profile
   * for the same person is a reconciliation problem nobody wants.
   */
  /**
   * What the provider holds about the applicant's own identity check.
   *
   * "Verified" is a claim; this is the record behind it. Reading it is audited,
   * because it is access to somebody's identity data.
   */
  applicantEvidence: (
    id: string,
  ): Promise<{
    decision: string;
    providerStatus: string;
    checkCount: number;
    readAt: string;
    fields: { label: string; value: string; group: string }[];
    files: { id: string; name: string; kind: string; status: string; sizeBytes: number; fromVerification: boolean }[];
  }> => apiFetch(`/admin/customers/${id}/verification-evidence`),

  /**
   * File a document on the customer's behalf.
   *
   * Paperwork arrives by email, by phone, in a branch — not only through the
   * app. Without this the only answer was "ask them to upload it", which stalls
   * a file on the applicant's availability rather than the bank's work.
   */
  /** Take a document off the client's file. Kept, and recorded with who and why. */
  removeDocument: (id: string, docId: string, kind: string, reason: string): Promise<unknown> =>
    apiFetch(`/admin/customers/${id}/documents/${docId}`, { method: 'DELETE', body: { kind, reason } }),

  /** A vault document's bytes, for the preview on the client card (DEV-98). */
  fetchDocumentBlob: (id: string, docId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/customers/${id}/documents/${docId}`),

  /** The dates a document turns on; both optional, none means it does not expire. */
  setDocumentDates: (id: string, docId: string, dates: { issuedAt: string | null; expiresAt: string | null }): Promise<unknown> =>
    apiFetch(`/admin/customers/${id}/documents/${docId}/dates`, { method: 'PUT', body: dates }),

  /** Refile a document under another type. */
  retypeDocument: (id: string, docId: string, type: string): Promise<unknown> =>
    apiFetch(`/admin/customers/${id}/documents/${docId}/type`, { method: 'PATCH', body: { type } }),

  /** Rename a document without uploading it again. */
  renameDocument: (id: string, docId: string, name: string): Promise<unknown> =>
    apiFetch(`/admin/customers/${id}/documents/${docId}/name`, { method: 'PATCH', body: { name } }),

  uploadCustomerDocument: async (
    id: string,
    file: File,
    slot: string,
    label?: string,
    onProgress?: (fraction: number) => void,
  ): Promise<{ id: string; name: string; sizeBytes: number }> => {
    const form = new FormData();
    // The name alone: a file picked from a folder is otherwise sent under its
    // path inside that folder. The gateway strips it too; this keeps the
    // request honest.
    form.append('file', file, file.name);
    const query = `slot=${encodeURIComponent(slot)}${
      label !== undefined && label !== '' ? `&label=${encodeURIComponent(label)}` : ''
    }`;
    return apiUpload(`/admin/customers/${id}/documents?${query}`, form, onProgress);
  },

  /** The provider's own KYC review document for the applicant. */
  applicantReport: (id: string): Promise<Blob> =>
    apiFetchBlob(`/admin/customers/${id}/verification-report`),

  companyReport: (id: string): Promise<Blob> =>
    apiFetchBlob(`/admin/customers/${id}/company-report`),

  /** The company's own sanctions screening result (DEV-89). */
  companyScreening: (id: string): Promise<CompanyScreening> =>
    apiFetch<CompanyScreening>(`/admin/customers/${id}/company-screening`),

  /** File the completed screening's report on the client now (compliance). */
  fileCompanyScreeningReport: (
    id: string,
  ): Promise<{ outcome: 'FILED' | 'ALREADY_FILED' | 'NOT_COMPLETED' | 'UNAVAILABLE'; screening: CompanyScreening }> =>
    apiFetch(`/admin/customers/${id}/company-screening/report`, { method: 'POST', body: {} }),

  /** Copy the check's artefacts into our vault so they survive the provider. */
  importApplicantFiles: (
    id: string,
  ): Promise<{
    imported: number;
    alreadyHeld: number;
    unavailable: number;
    files: { name: string; outcome: string; detail?: string; sizeBytes?: number }[];
  }> =>
    apiFetch(`/admin/customers/${id}/verification-files/import`, { method: 'POST' }),

  /** The files the hosted check left at the provider for the applicant. */
  applicantFiles: (
    id: string,
  ): Promise<
    { id: string; name: string; kind: string; status: string; sizeBytes: number; fromVerification: boolean }[]
  > => apiFetch(`/admin/customers/${id}/verification-files`),

  /** One of those files' bytes, fetched with the session's token. */
  applicantFile: (id: string, fileId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/customers/${id}/verification-files/${fileId}`),

  issueApplicantLink: (id: string): Promise<{ verificationLink: string }> =>
    apiFetch(`/admin/customers/${id}/verification-link`, { method: 'POST' }),

  issuePartyLink: (id: string, personId: string): Promise<{ verificationLink: string }> =>
    apiFetch(`/admin/customers/${id}/verification-parties/${personId}/link`, {
      method: 'POST',
    }),

  /**
   * What the provider holds about one party's identity check. Reading it is
   * audit-logged server-side — this is identity-document data.
   */
  partyEvidence: (id: string, personId: string): Promise<PartyEvidence> =>
    apiFetch<PartyEvidence>(
      `/admin/customers/${id}/verification-parties/${personId}/evidence`,
    ),

  /**
   * The provider's KYC review document for one party, as a Blob. Rendered in a
   * sandboxed frame — it is provider-generated HTML built around
   * customer-supplied names, so it is never trusted as page markup.
   */
  partyReport: (id: string, personId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/customers/${id}/verification-parties/${personId}/report`),

  /** The check artefacts Norvix HOLDS for this party — served from our vault. */
  heldPartyFiles: (id: string, personId: string): Promise<HeldFile[]> =>
    apiFetch<HeldFile[]>(
      `/admin/customers/${id}/verification-parties/${personId}/identity-files`,
    ),

  /** One held file's bytes, from our own store. */
  heldFileBlob: (id: string, docId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/customers/${id}/documents/${docId}`),

  /** Files a completed identity check produced at the provider. */
  partyFiles: (id: string, personId: string): Promise<ProviderFile[]> =>
    apiFetch<ProviderFile[]>(
      `/admin/customers/${id}/verification-parties/${personId}/files`,
    ),

  /**
   * One identity file's bytes — a passport image, a liveness photo, a report.
   * Every fetch is audit-logged server-side.
   */
  partyFileBlob: (id: string, personId: string, fileId: string): Promise<Blob> =>
    apiFetchBlob(
      `/admin/customers/${id}/verification-parties/${personId}/files/${encodeURIComponent(fileId)}`,
    ),

  /**
   * Import the check's artefacts into Norvix's own vault, so the file does not
   * depend on the provider being reachable — or on it keeping them.
   */
  importPartyFiles: (
    id: string,
    personId: string,
    partyName: string,
  ): Promise<{
    imported: number;
    alreadyHeld: number;
    unavailable: number;
    files: { name: string; outcome: string; detail?: string; sizeBytes?: number }[];
  }> =>
    apiFetch(`/admin/customers/${id}/verification-parties/${personId}/files/import`, {
      method: 'POST',
      body: { partyName },
    }),

  /**
   * Remove a duplicate/stray related person: unlink at the provider
   * (best-effort) and delete the reference row. Audit-logged server-side.
   */
  removeParty: (
    id: string,
    personId: string,
    reason: string,
  ): Promise<{ removed: boolean; vendorUnlinked: boolean; message: string }> =>
    apiFetch(`/admin/customers/${id}/verification-parties/${personId}`, {
      method: 'DELETE',
      body: { reason },
    }),
};
